import { AnimatedNumber } from '@/components/Text';
import { SCORE_CIRCLE_CONFIG } from '../../config';
import { SkeletonLoader } from '@/components/Loaders';
import { useState, useEffect } from 'react';
import type { ScoreReadoutProps } from '../../types';
import { SkillLevelTag } from '@/components/Tags';

/**
 * Renders the centered score headline and value.
 */
export const ScoreReadout: React.FC<ScoreReadoutProps> = ({
  isLoading,
  isPrinting,
  progressProviderValue,
  skillLevel,
  simulationType,
  useAnimatedNumbers,
  value,
}) => {
  // Local state to control the displayed value for animation purposes. When
  // loading, it is set to 0 to show the loading state, and when not loading,
  // it is set to the actual value to trigger the animation.
  const [displayValue, setDisplayValue] = useState(value);

  // Update the display value based on loading state and actual value changes
  useEffect(() => {
    if (isLoading) setDisplayValue(0);
    else setDisplayValue(value);
  }, [isLoading, value]);

  // Render
  return (
    <div className="relative -mt-2 flex flex-col items-center justify-center gap-6 text-center">
      <p
        aria-label={`Your score: ${displayValue}%`}
        className="pointer-events-none text-center font-display font-bold"
      >
        <span className="block text-xl text-neutral-gray-800">Your score</span>
        {useAnimatedNumbers ? (
          <span className="flex items-center justify-start">
            {isLoading ? (
              <SkeletonLoader asTag="span" className="mt-1 h-[56px]" />
            ) : (
              <AnimatedNumber
                className="block text-6xl text-neutral-black"
                delay={SCORE_CIRCLE_CONFIG.ANIMATIONS.delay}
                duration={SCORE_CIRCLE_CONFIG.ANIMATIONS.duration}
                easing={SCORE_CIRCLE_CONFIG.ANIMATIONS.easing}
                formatValue={(v) => Math.round(v).toString() + '%'}
                isPrinting={isPrinting}
                value={displayValue}
              />
            )}
          </span>
        ) : (
          <span className="block text-6xl text-neutral-black">{progressProviderValue}%</span>
        )}
      </p>

      {simulationType === 'role-play' &&
        (isLoading ? (
          <SkeletonLoader className="h-6 w-24" />
        ) : (
          <SkillLevelTag
            className="animate-fade-in animate-duration-fast animate-iteration-count-once"
            skillLevel={skillLevel}
          />
        ))}
    </div>
  );
};

ScoreReadout.displayName = 'ScoreReadout';

export default ScoreReadout;
